import { useEffect } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { LineupEntry } from '../../lib/selectors';
import { SortableLineupRow } from './SortableLineupRow';
import { BoltIcon, CloseIcon } from '../Icon';
import styles from './LineupDrawer.module.css';

interface LineupDrawerProps {
  entries: LineupEntry[];
  onClose: () => void;
  onToggleTask: (taskId: string) => void;
  onSelectTask: (taskId: string) => void;
  onToggleLineup: (taskId: string) => void;
  onCursor: (title: string) => void;
  onClear: () => void;
  /** Persist a new Lineup ordering (array of task ids, top-to-bottom). */
  onReorder: (orderedTaskIds: string[]) => void;
}

export function LineupDrawer({
  entries,
  onClose,
  onToggleTask,
  onSelectTask,
  onToggleLineup,
  onCursor,
  onClear,
  onReorder,
}: LineupDrawerProps) {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = entries.map((e) => e.task.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(ids, from, to));
  };

  const openCount = entries.filter((e) => !e.task.done).length;

  return (
    <>
      <button
        type="button"
        className={styles.overlay}
        onClick={onClose}
        aria-label="Close Lineup"
      />
      <aside className={`${styles.drawer} sb-scroll`} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <BoltIcon size={16} className={styles.headIcon} />
          <div className={styles.headText}>
            <div className={styles.title}>Lineup</div>
            <div className={styles.subtitle}>
              {openCount} to do{entries.length > openCount ? ` · ${entries.length - openCount} done` : ''}
            </div>
          </div>
          {entries.length > 0 && (
            <button type="button" className={styles.clear} onClick={onClear}>
              Clear
            </button>
          )}
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className={styles.body}>
          {entries.length === 0 ? (
            <div className={styles.empty}>
              <BoltIcon size={24} className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>Your Lineup is empty</p>
              <p className={styles.emptyHint}>
                Add tasks with the ⚡ button to build what you’re working on now.
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={entries.map((e) => e.task.id)}
                strategy={verticalListSortingStrategy}
              >
                {entries.map((entry) => (
                  <SortableLineupRow
                    key={entry.task.id}
                    entry={entry}
                    onToggleTask={onToggleTask}
                    onSelectTask={onSelectTask}
                    onToggleLineup={onToggleLineup}
                    onCursor={onCursor}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </aside>
    </>
  );
}
